import React from "react";
import {
  Alert,
  Box,
  Typography,
  Divider,
  Grid,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  LinearProgress,
  Chip,
  Stack,
  alpha,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import Iconify from "../../../components/iconify";
import {
  useGetTenantInformationQuery,
  useGetTenantSubscriptionTerminationPreviewQuery,
  useRevokeTenantSubscriptionTerminationMutation,
  useTerminateTenantSubscriptionMutation,
  useUpdateTenantMutation,
} from "../../../features/tenants/tenantsApiSlice";
import { useGetMembersQuery } from "../../../features/members/membersApiSlice";
import Loading from "../../../components/spinners/Loading";
import ModuleAddDialog from "./ModuleAddDialog";
import { useDispatch } from "react-redux";
import { setDialog } from "../../../features/dialog/dialogSlice";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FEATURE_KEYS,
  PLAN_IDS,
  getPlanIndex,
  hasFeatureAccess,
  normalizePlanId,
} from "../../../utils/planFeatures";
import usePlanOptions from "../../../hooks/usePlanOptions";

const inferPlanIdFromLimits = (tenant, planOptions) => {
  if (!tenant) return null;
  const matchingPlan = planOptions.find(
    (plan) =>
      plan.allowedUsers === tenant.allowedUsers &&
      plan.allowedSites === tenant.allowedSites &&
      plan.allowedMembers === tenant.allowedMembers,
  );

  return matchingPlan?.id ?? null;
};

const ModuleSettings = () => {
  const PLAN_OPTIONS = usePlanOptions();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: tenant, isLoading: isLoadingTenant } =
    useGetTenantInformationQuery();
  const {
    data: terminationPreviewData,
    isLoading: isLoadingTerminationPreview,
    isError: isTerminationPreviewError,
    error: terminationPreviewError,
    refetch: refetchTerminationPreview,
  } = useGetTenantSubscriptionTerminationPreviewQuery();
  const { data: members, isLoading: isLoadingMembers } = useGetMembersQuery();
  const [updateTenant, { isLoading: isUpdatingTenant }] =
    useUpdateTenantMutation();
  const [terminateTenantSubscription, { isLoading: isTerminatingSubscription }] =
    useTerminateTenantSubscriptionMutation();
  const [revokeTenantSubscriptionTermination, { isLoading: isRevokingTermination }] =
    useRevokeTenantSubscriptionTerminationMutation();
  const dispatch = useDispatch();
  const [isPlanDialogOpen, setIsPlanDialogOpen] = React.useState(false);
  const [selectedPlanId, setSelectedPlanId] = React.useState("");
  const [isConfirmingUpgrade, setIsConfirmingUpgrade] = React.useState(false);
  const [isTerminationDialogOpen, setIsTerminationDialogOpen] =
    React.useState(false);
  const [isRevokeTerminationDialogOpen, setIsRevokeTerminationDialogOpen] =
    React.useState(false);
  const [terminationReason, setTerminationReason] = React.useState("");
  const [confirmTenantName, setConfirmTenantName] = React.useState("");
  const [terminationAcknowledged, setTerminationAcknowledged] =
    React.useState(false);

  // Calculate actual counts
  const userCount = tenant?.users?.length || 0;
  const siteCount = tenant?.sites?.length || 0;
  const memberCount = members?.length || 0;
  const allowedUsers = tenant?.allowedUsers ?? null;
  const allowedSites = tenant?.allowedSites ?? null;
  const allowedMembers = tenant?.allowedMembers ?? null;
  const isUsersLimited = Number.isFinite(allowedUsers);
  const isSitesLimited = Number.isFinite(allowedSites);
  const isMembersLimited = Number.isFinite(allowedMembers);

  // Calculate usage percentages
  const userPercentage =
    isUsersLimited && allowedUsers > 0 ? (userCount / allowedUsers) * 100 : 0;
  const sitePercentage =
    isSitesLimited && allowedSites > 0 ? (siteCount / allowedSites) * 100 : 0;
  const memberPercentage =
    isMembersLimited && allowedMembers > 0
      ? (memberCount / allowedMembers) * 100
      : 0;

  const storedPlanRaw = tenant?.billingPlan || "";
  const storedPlanId = storedPlanRaw ? normalizePlanId(storedPlanRaw) : "";
  const inferredPlanId = inferPlanIdFromLimits(tenant, PLAN_OPTIONS);
  const currentPlanId = storedPlanId || inferredPlanId || PLAN_IDS.BASIS;
  const currentPlan =
    PLAN_OPTIONS.find((plan) => plan.id === currentPlanId) || PLAN_OPTIONS[0];
  const currentPlanIndex = getPlanIndex(currentPlanId);
  const isPlanUnknown = !storedPlanRaw && !inferredPlanId;
  const upgradeOptions = PLAN_OPTIONS.filter(
    (plan) => getPlanIndex(plan.id) > currentPlanIndex,
  );
  const canUpgrade = upgradeOptions.length > 0;
  const hasEDojangAccess = hasFeatureAccess(currentPlanId, FEATURE_KEYS.E_DOJANG);
  const eDojangTargetPlan = PLAN_IDS.ELITE;
  const canUpgradeToEDojang =
    !hasEDojangAccess &&
    canUpgrade &&
    upgradeOptions.some((plan) => plan.id === eDojangTargetPlan);
  const requestedUpgradeRaw = React.useMemo(
    () => String(searchParams.get("upgrade") || "").trim().toLowerCase(),
    [searchParams],
  );
  const requestedUpgradePlan = requestedUpgradeRaw
    ? normalizePlanId(requestedUpgradeRaw)
    : "";

  React.useEffect(() => {
    if (!requestedUpgradePlan || !canUpgrade) return;
    const requestedOption = upgradeOptions.find(
      (plan) => plan.id === requestedUpgradePlan,
    );
    const fallbackOption = upgradeOptions[0];
    if (!requestedOption && !fallbackOption) return;

    setSelectedPlanId(requestedOption?.id || fallbackOption.id);
    setIsConfirmingUpgrade(false);
    setIsPlanDialogOpen(true);
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        nextParams.delete("upgrade");
        return nextParams;
      },
      { replace: true },
    );
  }, [requestedUpgradePlan, canUpgrade, upgradeOptions, setSearchParams]);

  const formatCountLabel = (value, singular, plural) => {
    if (!Number.isFinite(value)) {
      return `Unbegrenzt ${plural}`;
    }
    return value === 1 ? `${value} ${singular}` : `${value} ${plural}`;
  };

  const currentLimitLabel =
    isUsersLimited && isSitesLimited && currentPlan?.limits
      ? currentPlan.limits
      : `${formatCountLabel(allowedUsers, "User", "User")} / ${formatCountLabel(
          allowedSites,
          "Standort",
          "Standorte",
        )}`;

  const currentMembersLabel =
    isMembersLimited && currentPlan?.members
      ? currentPlan.members
      : formatCountLabel(allowedMembers, "Mitglied", "Mitglieder");
  const subscriptionStatus = String(tenant?.subscription?.status || "active")
    .trim()
    .toLowerCase();
  const previewStatus = String(
    terminationPreviewData?.subscription?.status || "",
  )
    .trim()
    .toLowerCase();
  const effectiveStatus = previewStatus || subscriptionStatus;
  const currentContractEnd =
    tenant?.subscription?.contractEnd ||
    terminationPreviewData?.subscription?.contractEnd ||
    null;
  const cancelledAt =
    tenant?.subscription?.cancelledAt ||
    terminationPreviewData?.subscription?.cancelledAt ||
    null;
  const projectedContractEnd =
    terminationPreviewData?.preview?.projectedContractEnd || null;
  const effectiveContractEnd =
    terminationPreviewData?.preview?.effectiveContractEnd ||
    currentContractEnd ||
    projectedContractEnd;
  const remainingDays =
    terminationPreviewData?.preview?.remainingDays ??
    terminationPreviewData?.remainingDays ??
    null;
  const canTerminate = Boolean(terminationPreviewData?.canTerminate);
  const isAlreadyCancelled =
    effectiveStatus === "cancelled" && Boolean(cancelledAt);
  const terminationErrorMessage =
    terminationPreviewError?.data?.message ||
    "Kündigungsdaten konnten nicht geladen werden.";
  const isOwnerBlocked = terminationPreviewError?.status === 403;
  const canOpenTerminateDialog =
    !isAlreadyCancelled &&
    !isOwnerBlocked &&
    !isTerminationPreviewError &&
    canTerminate;
  const canOpenRevokeDialog =
    isAlreadyCancelled &&
    !isOwnerBlocked &&
    !isTerminationPreviewError &&
    !isRevokingTermination;
  const confirmTenantMatches =
    String(confirmTenantName || "").trim().toLowerCase() ===
    String(tenant?.tenantName || "").trim().toLowerCase();
  const canSubmitTermination =
    Boolean(tenant?.tenantName) &&
    terminationAcknowledged &&
    confirmTenantMatches &&
    !isTerminatingSubscription;

  const formatDate = (value) => {
    if (!value) return "–";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "–";
    return new Intl.DateTimeFormat("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const openPlanDialog = () => {
    if (!canUpgrade) return;
    setSelectedPlanId(upgradeOptions[0]?.id ?? "");
    setIsConfirmingUpgrade(false);
    setIsPlanDialogOpen(true);
  };

  const openEDojangUpgradeDialog = () => {
    if (!canUpgradeToEDojang) return;
    setSelectedPlanId(eDojangTargetPlan);
    setIsConfirmingUpgrade(false);
    setIsPlanDialogOpen(true);
  };

  const closePlanDialog = () => {
    setIsPlanDialogOpen(false);
    setIsConfirmingUpgrade(false);
  };

  const handleUpgradePlan = async () => {
    const selectedPlan = PLAN_OPTIONS.find(
      (plan) => plan.id === selectedPlanId,
    );
    if (!selectedPlan || selectedPlan.id === currentPlanId) {
      return;
    }

    try {
      const result = await updateTenant({
        billingPlan: selectedPlan.id,
        allowedUsers: selectedPlan.allowedUsers,
        allowedSites: selectedPlan.allowedSites,
        allowedMembers: selectedPlan.allowedMembers,
      }).unwrap();

      if (result?.type === "success") {
        dispatch(
          setDialog({
            title: "Tarif aktualisiert",
            type: "SUCCESS",
            data: {
              successText:
                result?.message ??
                "Ihr Tarif wurde erfolgreich aktualisiert.",
            },
          }),
        );
      }
      closePlanDialog();
    } catch (error) {
      console.log("error :>> ", error);
      dispatch(
        setDialog({
          title: "Fehler",
          type: "ERROR",
          data: {
            errorText:
              "Fehler beim Tarif-Upgrade. Bitte versuchen Sie es erneut.",
          },
        }),
      );
    }
  };

  const handleContinueUpgrade = () => {
    if (!selectedPlanId) return;
    setIsConfirmingUpgrade(true);
  };

  const handleBackToSelection = () => {
    setIsConfirmingUpgrade(false);
  };

  const openTerminationDialog = () => {
    if (!canOpenTerminateDialog) return;
    setTerminationReason("");
    setConfirmTenantName("");
    setTerminationAcknowledged(false);
    setIsTerminationDialogOpen(true);
  };

  const closeTerminationDialog = () => {
    setIsTerminationDialogOpen(false);
    setTerminationReason("");
    setConfirmTenantName("");
    setTerminationAcknowledged(false);
  };

  const handleTerminateSubscription = async () => {
    if (!canSubmitTermination) return;

    try {
      const result = await terminateTenantSubscription({
        reason: terminationReason,
        confirmTenantName: confirmTenantName.trim(),
      }).unwrap();

      dispatch(
        setDialog({
          title: "Kündigung eingereicht",
          type: "SUCCESS",
          data: {
            successText:
              result?.accessUntil
                ? `Ihr Vertrag endet am ${formatDate(
                    result.accessUntil,
                  )}. Bis dahin bleibt Ihr Zugriff vollständig aktiv.`
                : "Ihre Kündigung wurde erfolgreich eingereicht.",
          },
        }),
      );
      closeTerminationDialog();
      refetchTerminationPreview();
    } catch (error) {
      dispatch(
        setDialog({
          title: "Kündigung nicht möglich",
          type: "ERROR",
          data: {
            errorText:
              error?.data?.message ||
              "Fehler beim Kündigen des Vertrags. Bitte versuchen Sie es erneut.",
          },
        }),
      );
    }
  };

  const openRevokeTerminationDialog = () => {
    if (!canOpenRevokeDialog) return;
    setIsRevokeTerminationDialogOpen(true);
  };

  const closeRevokeTerminationDialog = () => {
    setIsRevokeTerminationDialogOpen(false);
  };

  const handleRevokeTermination = async () => {
    if (!canOpenRevokeDialog) return;

    try {
      const result = await revokeTenantSubscriptionTermination().unwrap();
      dispatch(
        setDialog({
          title: "Kündigung zurückgenommen",
          type: "SUCCESS",
          data: {
            successText:
              result?.subscription?.contractEnd
                ? `Ihr Vertrag ist wieder aktiv. Aktuelles Vertragsende: ${formatDate(
                    result.subscription.contractEnd,
                  )}.`
                : "Ihre Kündigung wurde erfolgreich zurückgenommen.",
          },
        }),
      );
      closeRevokeTerminationDialog();
      refetchTerminationPreview();
    } catch (error) {
      dispatch(
        setDialog({
          title: "Reaktivierung nicht möglich",
          type: "ERROR",
          data: {
            errorText:
              error?.data?.message ||
              "Fehler beim Zurücknehmen der Kündigung. Bitte versuchen Sie es erneut.",
          },
        }),
      );
    }
  };

  const selectedPlan = PLAN_OPTIONS.find((plan) => plan.id === selectedPlanId);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {(isLoadingTenant || isLoadingMembers || isLoadingTerminationPreview) && (
        <Loading />
      )}

      <Box sx={{ mb: 4 }}>
        <Typography variant="h3" sx={{ mb: 1, fontWeight: 600 }}>
          Module verwalten
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Verwalten Sie Ihre gebuchten Module und erweitern Sie Ihre Kapazitäten
        </Typography>
      </Box>

      <Divider sx={{ mb: 4 }} />

      <Card
        sx={{
          borderRadius: 3,
          transition: "all 0.3s ease",
          border: "1px solid",
          borderColor: "divider",
          mb: 4,
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: (theme) => theme.shadows[6],
          },
        }}
      >
        <CardHeader
          avatar={
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
              }}
            >
              <Iconify
                icon="mdi:tag-outline"
                width={30}
                sx={{ color: "info.main" }}
              />
            </Box>
          }
          title={
            <Typography variant="h5" fontWeight={600}>
              Tarif & Upgrade
            </Typography>
          }
          subheader={
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                label={currentPlan?.title ?? "Unbekannter Tarif"}
                color="info"
                size="small"
              />
              {isAlreadyCancelled ? (
                <Chip
                  label={`Gekündigt bis ${formatDate(effectiveContractEnd)}`}
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              ) : null}
              {isPlanUnknown ? (
                <Chip
                  label="Tarif unklar"
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              ) : canUpgrade ? (
                <Chip
                  label="Upgrade möglich"
                  color="success"
                  size="small"
                  variant="outlined"
                />
              ) : (
                <Chip
                  label="Höchster Tarif"
                  color="default"
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          }
        />
        <CardContent>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Aktuelle Limits
              </Typography>
              <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
                {currentLimitLabel}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {currentMembersLabel}
              </Typography>
            </Box>
            {currentPlan?.price && (
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Monatlicher Preis
                </Typography>
                <Typography variant="h6" fontWeight={600}>
                  {currentPlan.price}
                </Typography>
              </Box>
            )}

            {isAlreadyCancelled ? (
              <Alert
                severity="warning"
                sx={{
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: (theme) => alpha(theme.palette.warning.main, 0.25),
                  bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
                }}
              >
                <Stack spacing={0.75}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Kündigung vorgemerkt
                  </Typography>
                  <Typography variant="body2">
                    Eingereicht am <strong>{formatDate(cancelledAt)}</strong>
                    {" "}mit Vertragsende am{" "}
                    <strong>{formatDate(effectiveContractEnd)}</strong>.
                  </Typography>
                  <Typography variant="body2">
                    Sie behalten bis zum Vertragsende den vollen Zugriff auf alle Daten und Funktionen.
                  </Typography>
                  {Number.isFinite(remainingDays) ? (
                    <Chip
                      size="small"
                      color="warning"
                      variant="outlined"
                      label={`Noch ${remainingDays} Tage aktiv`}
                      sx={{ width: "fit-content" }}
                    />
                  ) : null}
                </Stack>
              </Alert>
            ) : null}

            {isTerminationPreviewError ? (
              <Alert
                severity={isOwnerBlocked ? "warning" : "error"}
                sx={{ borderRadius: 2 }}
                action={
                  !isOwnerBlocked ? (
                    <Button
                      color="inherit"
                      size="small"
                      onClick={refetchTerminationPreview}
                    >
                      Erneut laden
                    </Button>
                  ) : null
                }
              >
                {terminationErrorMessage}
              </Alert>
            ) : null}
          </Stack>
        </CardContent>
        <CardActions sx={{ p: 2, pt: 0 }}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: "100%" }}
          >
            <Button
              variant="contained"
              color="info"
              onClick={openPlanDialog}
              disabled={!canUpgrade}
              startIcon={<Iconify icon="mdi:arrow-up-bold-circle-outline" />}
              sx={{ py: 1.2, fontWeight: 600, textTransform: "none" }}
            >
              Tarif upgraden
            </Button>

            {isAlreadyCancelled ? (
              <Button
                variant="contained"
                color="warning"
                disabled={!canOpenRevokeDialog}
                onClick={openRevokeTerminationDialog}
                startIcon={<Iconify icon="mdi:undo-variant" />}
                sx={{
                  py: 1.1,
                  px: 1.5,
                  fontWeight: 600,
                  textTransform: "none",
                  justifyContent: { xs: "flex-start", sm: "center" },
                }}
              >
                Kündigung zurücknehmen
              </Button>
            ) : (
              <Button
                variant="text"
                color="inherit"
                disabled={!canOpenTerminateDialog}
                onClick={openTerminationDialog}
                startIcon={<Iconify icon="mdi:close-circle-outline" />}
                sx={{
                  py: 1.1,
                  px: 1.5,
                  fontWeight: 500,
                  textTransform: "none",
                  opacity: 0.74,
                  justifyContent: { xs: "flex-start", sm: "center" },
                }}
              >
                Vertrag kündigen
              </Button>
            )}
          </Stack>
        </CardActions>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              minHeight: 320,
              transition: "all 0.3s ease",
              border: "1px solid",
              borderColor: "divider",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: (theme) => theme.shadows[8],
              },
            }}
          >
            <CardHeader
              avatar={
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  <Iconify
                    icon="eva:person-add-fill"
                    width={32}
                    sx={{ color: "primary.main" }}
                  />
                </Box>
              }
              title={
                <Typography variant="h5" fontWeight={600}>
                  Benutzer-Module
                </Typography>
              }
              subheader={
                <Chip
                  icon={<Iconify icon="eva:trending-up-fill" width={16} />}
                  label={`${userCount} von ${
                    isUsersLimited ? allowedUsers : "unbegrenzt"
                  } genutzt`}
                  size="small"
                  color={isUsersLimited && userPercentage > 80 ? "warning" : "success"}
                  sx={{ mt: 1 }}
                />
              }
            />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Auslastung
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={
                        isUsersLimited && userPercentage > 80
                          ? "warning.main"
                          : "success.main"
                      }
                    >
                      {isUsersLimited ? `${userPercentage.toFixed(0)}%` : "Unbegrenzt"}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      isUsersLimited ? Math.min(userPercentage, 100) : 100
                    }
                    color={isUsersLimited && userPercentage > 80 ? "warning" : "success"}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.1),
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  System-Benutzer für die Verwaltung der Plattform
                </Typography>
              </Stack>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <ModuleAddDialog tenant={tenant} module={"USER"} />
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              minHeight: 320,
              transition: "all 0.3s ease",
              border: "1px solid",
              borderColor: "divider",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: (theme) => theme.shadows[8],
              },
            }}
          >
            <CardHeader
              avatar={
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                  }}
                >
                  <Iconify
                    icon="eva:people-fill"
                    width={32}
                    sx={{ color: "success.main" }}
                  />
                </Box>
              }
              title={
                <Typography variant="h5" fontWeight={600}>
                  Mitglieder-Module
                </Typography>
              }
              subheader={
                <Chip
                  icon={<Iconify icon="eva:trending-up-fill" width={16} />}
                  label={`${memberCount} von ${
                    isMembersLimited ? allowedMembers : "unbegrenzt"
                  } genutzt`}
                  size="small"
                  color={
                    isMembersLimited && memberPercentage > 80
                      ? "warning"
                      : "success"
                  }
                  sx={{ mt: 1 }}
                />
              }
            />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Auslastung
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={
                        isMembersLimited && memberPercentage > 80
                          ? "warning.main"
                          : "success.main"
                      }
                    >
                      {isMembersLimited
                        ? `${memberPercentage.toFixed(0)}%`
                        : "Unbegrenzt"}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      isMembersLimited ? Math.min(memberPercentage, 100) : 100
                    }
                    color={
                      isMembersLimited && memberPercentage > 80
                        ? "warning"
                        : "success"
                    }
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.1),
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Erweitern Sie Ihre Mitgliederkapazität mit flexiblen Paketen
                </Typography>
              </Stack>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <ModuleAddDialog tenant={tenant} module={"MEMBER"} />
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              minHeight: 320,
              transition: "all 0.3s ease",
              border: "1px solid",
              borderColor: "divider",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: (theme) => theme.shadows[8],
              },
            }}
          >
            <CardHeader
              avatar={
                <Box
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: (theme) =>
                      alpha(theme.palette.secondary.main, 0.1),
                  }}
                >
                  <Iconify
                    icon="eva:briefcase-fill"
                    width={32}
                    sx={{ color: "secondary.main" }}
                  />
                </Box>
              }
              title={
                <Typography variant="h5" fontWeight={600}>
                  Standort-Module
                </Typography>
              }
              subheader={
                <Chip
                  icon={<Iconify icon="eva:trending-up-fill" width={16} />}
                  label={`${siteCount} von ${
                    isSitesLimited ? allowedSites : "unbegrenzt"
                  } genutzt`}
                  size="small"
                  color={isSitesLimited && sitePercentage > 80 ? "warning" : "success"}
                  sx={{ mt: 1 }}
                />
              }
            />
            <CardContent>
              <Stack spacing={2}>
                <Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Auslastung
                    </Typography>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={
                        isSitesLimited && sitePercentage > 80
                          ? "warning.main"
                          : "success.main"
                      }
                    >
                      {isSitesLimited ? `${sitePercentage.toFixed(0)}%` : "Unbegrenzt"}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      isSitesLimited ? Math.min(sitePercentage, 100) : 100
                    }
                    color={isSitesLimited && sitePercentage > 80 ? "warning" : "success"}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: (theme) => alpha(theme.palette.grey[500], 0.1),
                    }}
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Fügen Sie weitere Standorte hinzu, um Ihr Geschäft zu
                  erweitern
                </Typography>
              </Stack>
            </CardContent>
            <CardActions sx={{ p: 2, pt: 0 }}>
              <ModuleAddDialog tenant={tenant} module={"SITE"} />
            </CardActions>
          </Card>
        </Grid>
      </Grid>

      <Card
        sx={{
          borderRadius: 3,
          transition: "all 0.3s ease",
          border: "1px solid",
          borderColor: "divider",
          mt: 4,
          mb: 4,
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: (theme) => theme.shadows[6],
          },
        }}
      >
        <CardHeader
          avatar={
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
              }}
            >
              <Iconify
                icon="fa-solid:yin-yang"
                width={28}
                sx={{ color: "warning.main" }}
              />
            </Box>
          }
          title={
            <Typography variant="h5" fontWeight={600}>
              E-Dojang Modulsteuerung
            </Typography>
          }
          subheader={
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Chip
                size="small"
                color={hasEDojangAccess ? "success" : "warning"}
                label={hasEDojangAccess ? "Aktiviert" : "Nicht aktiviert"}
              />
              <Chip
                size="small"
                variant="outlined"
                label="Verfügbar im Elite-Tarif"
              />
            </Stack>
          }
        />
        <CardContent>
          <Stack spacing={1.75}>
            <Typography variant="body2" color="text.secondary">
              Zentraler Steuerpunkt für das E-Dojang. Hier sehen Sie den
              Aktivierungsstatus und gelangen direkt in den Verwaltungsbereich.
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip size="small" label="Prüfungsprogramm" variant="outlined" />
              <Chip size="small" label="Lehrvideos" variant="outlined" />
              <Chip size="small" label="Lernmaterialien" variant="outlined" />
              <Chip size="small" label="Lernpfade" variant="outlined" />
            </Stack>

            {hasEDojangAccess ? (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                E-Dojang ist aktiv. Die Domain-Listen können direkt gepflegt werden.
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ borderRadius: 2 }}>
                E-Dojang ist in Ihrem aktuellen Tarif nicht freigeschaltet.
                Aktivierung erfolgt über ein Upgrade auf Elite.
              </Alert>
            )}
          </Stack>
        </CardContent>
        <CardActions sx={{ p: 2, pt: 0 }}>
          <Button
            variant="contained"
            color={hasEDojangAccess ? "success" : "warning"}
            onClick={
              hasEDojangAccess
                ? () => navigate("/dashboard/e-dojang")
                : openEDojangUpgradeDialog
            }
            disabled={!hasEDojangAccess && !canUpgradeToEDojang}
            startIcon={
              <Iconify
                icon={
                  hasEDojangAccess
                    ? "mdi:open-in-new"
                    : "mdi:arrow-up-bold-circle-outline"
                }
              />
            }
            sx={{ py: 1.1, px: 1.8, fontWeight: 600, textTransform: "none" }}
          >
            {hasEDojangAccess ? "E-Dojang öffnen" : "Auf Elite upgraden"}
          </Button>
        </CardActions>
      </Card>

      <Dialog
        open={isTerminationDialogOpen}
        onClose={closeTerminationDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
              }}
            >
              <Iconify
                icon="mdi:close-circle-outline"
                width={22}
                sx={{ color: "warning.main" }}
              />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Vertrag kündigen
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Kündigung wird sofort vorgemerkt, Zugriff bleibt bis Vertragsende aktiv.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: (theme) => alpha(theme.palette.info.main, 0.2),
              }}
            >
              Ihre Daten werden nicht gelöscht. Bis zum Vertragsende am{" "}
              <strong>{formatDate(projectedContractEnd || effectiveContractEnd)}</strong>
              {" "}können Sie die Software vollständig weiter nutzen.
            </Alert>

            <Card
              variant="outlined"
              sx={{
                borderRadius: 2,
                borderColor: (theme) => alpha(theme.palette.warning.main, 0.3),
              }}
            >
              <CardContent sx={{ p: 2 }}>
                <Stack spacing={1.25}>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Kündigung wird eingetragen am
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatDate(
                        terminationPreviewData?.preview?.terminationDate || new Date(),
                      )}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Vertragsende
                    </Typography>
                    <Typography variant="body2" fontWeight={700} color="warning.dark">
                      {formatDate(projectedContractEnd || effectiveContractEnd)}
                    </Typography>
                  </Stack>
                  <Stack direction="row" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Restlaufzeit
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {Number.isFinite(remainingDays) ? `${remainingDays} Tage` : "–"}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              label="Grund für die Kündigung (optional)"
              value={terminationReason}
              onChange={(event) => setTerminationReason(event.target.value)}
              placeholder="Ihr Feedback hilft uns, das Produkt zu verbessern."
            />

            <TextField
              fullWidth
              size="small"
              label={`Zur Bestätigung "${tenant?.tenantName || ""}" eingeben`}
              value={confirmTenantName}
              onChange={(event) => setConfirmTenantName(event.target.value)}
              error={Boolean(confirmTenantName) && !confirmTenantMatches}
              helperText={
                confirmTenantName && !confirmTenantMatches
                  ? "Mandantenname stimmt nicht überein."
                  : "Sicherheitsabfrage gegen versehentliche Kündigungen."
              }
            />

            <FormControlLabel
              sx={{ alignItems: "flex-start", m: 0 }}
              control={
                <Checkbox
                  checked={terminationAcknowledged}
                  onChange={(event) =>
                    setTerminationAcknowledged(event.target.checked)
                  }
                  color="warning"
                  sx={{ mt: -0.3 }}
                />
              }
              label={
                <Typography variant="body2" color="text.secondary">
                  Ich habe verstanden, dass die Kündigung verbindlich eingetragen wird und der Zugriff bis zum
                  Vertragsende bestehen bleibt.
                </Typography>
              }
            />
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={closeTerminationDialog} variant="outlined">
            Abbrechen
          </Button>
          <Button
            onClick={handleTerminateSubscription}
            variant="contained"
            color="warning"
            disabled={!canSubmitTermination}
            startIcon={<Iconify icon="mdi:check-circle-outline" />}
            sx={{ fontWeight: 600 }}
          >
            Kündigung verbindlich einreichen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isRevokeTerminationDialogOpen}
        onClose={closeRevokeTerminationDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
              }}
            >
              <Iconify
                icon="mdi:undo-variant"
                width={22}
                sx={{ color: "warning.main" }}
              />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                Kündigung zurücknehmen
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Der Vertrag wird wieder auf aktiv gesetzt.
              </Typography>
            </Box>
          </Stack>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <Alert
              severity="warning"
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: (theme) => alpha(theme.palette.warning.main, 0.3),
                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.08),
              }}
            >
              Die vorgemerkte Kündigung wird entfernt. Ihr Zugang bleibt aktiv und
              der Vertrag läuft regulär weiter.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Aktuell hinterlegtes Vertragsende:{" "}
              <strong>{formatDate(effectiveContractEnd)}</strong>
            </Typography>
          </Stack>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={closeRevokeTerminationDialog} variant="outlined">
            Abbrechen
          </Button>
          <Button
            onClick={handleRevokeTermination}
            variant="contained"
            color="warning"
            disabled={isRevokingTermination}
            startIcon={<Iconify icon="mdi:check-circle-outline" />}
            sx={{ fontWeight: 600 }}
          >
            Kündigung zurücknehmen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isPlanDialogOpen}
        onClose={closePlanDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
              }}
            >
              <Iconify
                icon="mdi:rocket-launch-outline"
                width={24}
                sx={{ color: "info.main" }}
              />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={600}>
                {isConfirmingUpgrade ? "Upgrade bestätigen" : "Tarif upgraden"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isConfirmingUpgrade
                  ? "Prüfen Sie die neuen Limits vor dem Upgrade"
                  : "Wählen Sie den passenden Tarif für Ihre Anforderungen"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {!isConfirmingUpgrade && (
            <Stack spacing={2}>
              {upgradeOptions.map((plan) => {
                const isSelected = selectedPlanId === plan.id;
                return (
                  <Card
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    sx={{
                      cursor: "pointer",
                      border: "2px solid",
                      borderColor: isSelected ? "info.main" : "divider",
                      bgcolor: isSelected
                        ? (theme) => alpha(theme.palette.info.main, 0.05)
                        : "background.paper",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "info.main",
                        boxShadow: (theme) => theme.shadows[4],
                      },
                    }}
                  >
                    <CardContent>
                      <Stack spacing={1}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="h6" fontWeight={600}>
                            {plan.title}
                          </Typography>
                          <Chip label={plan.price} color="info" size="small" />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          {plan.limits}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {plan.members}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          )}
          {isConfirmingUpgrade && selectedPlan && (
            <Card
              sx={{
                border: "2px solid",
                borderColor: "info.main",
                bgcolor: (theme) => alpha(theme.palette.info.main, 0.05),
              }}
            >
              <CardContent>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="h6" fontWeight={600}>
                      {selectedPlan.title}
                    </Typography>
                    <Chip label={selectedPlan.price} color="info" size="small" />
                  </Stack>
                  <Divider />
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Neue Limits
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {formatCountLabel(
                        selectedPlan.allowedUsers,
                        "User",
                        "User",
                      )}{" "}
                      /{" "}
                      {formatCountLabel(
                        selectedPlan.allowedSites,
                        "Standort",
                        "Standorte",
                      )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCountLabel(
                        selectedPlan.allowedMembers,
                        "Mitglied",
                        "Mitglieder",
                      )}
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={isConfirmingUpgrade ? handleBackToSelection : closePlanDialog}
            variant="outlined"
            sx={{ minWidth: 120 }}
          >
            {isConfirmingUpgrade ? "Zurück" : "Abbrechen"}
          </Button>
          {!isConfirmingUpgrade && (
            <Button
              onClick={handleContinueUpgrade}
              variant="contained"
              color="info"
              sx={{ minWidth: 180, fontWeight: 600 }}
              startIcon={<Iconify icon="mdi:arrow-right-bold-circle-outline" />}
              disabled={!selectedPlanId}
            >
              Weiter
            </Button>
          )}
          {isConfirmingUpgrade && (
            <Button
              onClick={handleUpgradePlan}
              variant="contained"
              color="info"
              sx={{ minWidth: 180, fontWeight: 600 }}
              startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
              disabled={!selectedPlanId || isUpdatingTenant}
            >
              Upgrade bestätigen
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ModuleSettings;
